<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <h6>{{ isEdit ? "編輯問題" : "新增問題" }}</h6>
          </div>
          <div class="card-body">
            <form @submit.prevent="handleSubmit">
              <div class="row">
                <div class="col-md-9">
                  <div class="form-group">
                    <label for="question" class="form-control-label"
                      >問題 *</label
                    >
                    <input
                      class="form-control"
                      type="text"
                      id="question"
                      v-model="form.question"
                      required
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="form-group">
                    <label class="form-control-label">狀態</label>
                    <div class="form-check form-switch mt-2">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="is_active"
                        v-model="form.is_active"
                      />
                      <label class="form-check-label" for="is_active"
                        >啟用</label
                      >
                    </div>
                  </div>
                </div>

                <div class="col-md-12">
                  <div class="form-group">
                    <div
                      class="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label for="answer" class="form-control-label mb-0"
                        >回答 * (Markdown)</label
                      >
                      <div class="btn-group btn-group-sm">
                        <input
                          type="file"
                          ref="videoInput"
                          class="d-none"
                          accept="video/*"
                          @change="handleVideoUpload"
                        />
                        <button
                          type="button"
                          class="btn btn-outline-secondary me-2"
                          @click="triggerVideoUpload"
                        >
                          <i class="fas fa-video me-1"></i> 插入影片
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            showPreview ? 'btn-primary' : 'btn-outline-primary'
                          "
                          @click="showPreview = !showPreview"
                        >
                          {{ showPreview ? "隱藏預覽" : "顯示預覽" }}
                        </button>
                      </div>
                    </div>

                    <div class="row">
                      <div :class="showPreview ? 'col-md-6' : 'col-md-12'">
                        <textarea
                          class="form-control"
                          rows="15"
                          id="answer"
                          v-model="form.answer"
                          placeholder="請輸入回答內容..."
                          required
                          style="font-family: monospace"
                        ></textarea>
                      </div>
                      <div v-show="showPreview" class="col-md-6">
                        <div
                          class="border p-4 rounded bg-white h-100"
                          style="
                            min-height: 380px;
                            max-height: 600px;
                            overflow-y: auto;
                          "
                        >
                          <div
                            v-if="!form.answer"
                            class="text-muted text-center py-5"
                          >
                            預覽區域
                          </div>
                          <div
                            v-else
                            v-html="renderMarkdown(form.answer)"
                            class="markdown-body"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-end mt-4">
                <button
                  type="button"
                  class="btn btn-light me-2"
                  @click="$router.push('/faqs')"
                >
                  取消
                </button>
                <button
                  type="submit"
                  class="btn btn-primary"
                  :disabled="loading"
                >
                  {{ loading ? "儲存中..." : "儲存" }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/utils/request";
import { marked } from "marked";
import Swal from "sweetalert2";
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

export default {
  name: "FaqForm",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(false);
    const showPreview = ref(true);
    const videoInput = ref(null);

    const form = ref({
      question: "",
      answer: "",
      is_active: true,
      sort_order: 0,
    });

    const isEdit = computed(() => route.params.id !== undefined);

    const renderMarkdown = (text) => {
      return marked(text || "");
    };

    const fetchFaq = async (id) => {
      try {
        const response = await request.get(`/api/admin/faqs/${id}`);
        form.value = response.data;
        // Ensure is_active is boolean
        form.value.is_active = !!form.value.is_active;
      } catch (error) {
        console.error("Failed to fetch FAQ", error);
        Swal.fire("錯誤", "無法載入資料", "error");
      }
    };

    const triggerVideoUpload = () => {
      videoInput.value.click();
    };

    const handleVideoUpload = async (event) => {
      const file = event.target.files[0];
      if (!file) return;

      try {
        const formData = new FormData();
        formData.append("file", file);

        // Show loading or inserting text
        const cursorPosition = document.getElementById("answer").selectionStart;
        const textBefore = form.value.answer.substring(0, cursorPosition);
        const textAfter = form.value.answer.substring(cursorPosition);

        // Upload
        const response = await request.post("/api/admin/upload", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        if (response.data.success) {
          const videoTag = `\n<video controls width="100%" src="${response.data.url}"></video>\n`;
          const newAnswer = textBefore + videoTag + textAfter;
          // eslint-disable-next-line require-atomic-updates
          form.value.answer = newAnswer;
          Swal.fire("成功", "影片插入成功", "success");
        } else {
          Swal.fire("錯誤", "上傳失敗", "error");
        }
      } catch (error) {
        console.error("Video upload failed", error);
        Swal.fire("錯誤", "影片上傳失敗", "error");
      } finally {
        // Reset input
        if (event && event.target) {
            // eslint-disable-next-line require-atomic-updates
            event.target.value = "";
        }
      }
    };

    const handleSubmit = async () => {
      loading.value = true;
      try {
        // Ensure sort_order is present, though controller might handle default
        const payload = { ...form.value };

        if (isEdit.value) {
          await request.put(`/api/admin/faqs/${route.params.id}`, payload);
        } else {
          await request.post("/api/admin/faqs", payload);
        }

        router.push("/faqs");
        Swal.fire("成功", "儲存成功", "success");
      } catch (error) {
        console.error("Failed to save FAQ", error);
        Swal.fire("錯誤", "儲存失敗", "error");
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      if (isEdit.value) {
        fetchFaq(route.params.id);
      }
    });

    return {
      form,
      loading,
      isEdit,
      showPreview,
      videoInput,
      triggerVideoUpload,
      handleVideoUpload,
      renderMarkdown,
      handleSubmit,
    };
  },
};
</script>
